import React, { useEffect, useState } from "react"
import MetaTags from "react-meta-tags"
import { connect } from "react-redux"
import { withTranslation } from "react-i18next"
import { Alert } from "antd"

// Store actions
import {
  getClientSetting,
  getUSERs,
  getUserBalance,
  inactiveClient,
} from "store/actions"

// Helpers
import { getCurrentUser } from "helpers/service_helper"
import { initializeSocket } from "helpers/signalr_helper"
import { getDashboardToken } from "helpers/backend_helper"

// Dashboard Layouts
import AdminDashboard from "./layouts/AdminDashboard"
import EngineerDashboard from "./layouts/EngineerDashboard"
import ExecutorDashboard from "./layouts/ExecutorDashboard"
import InstallerDashboard from "./layouts/InstallerDashboard"

import AccountantDashboard from "./layouts/AccountantDashboard"

// Styles
import "./Dashboard.css"
import GasCoDashboard from "./layouts/GasCoDashboard"

const Dashboard = props => {
  const {
    userInfo,
    onGetClientSetting,
    clientSetting,
    onInactiveClient,
    clientLoading,
  } = props

  const search = props.location.search
  const params = new URLSearchParams(search)
  const returnFromBank = params.get("result")

  const [infoMessage, setInfoMessage] = useState(null)
  const [returnMessage, setReturnMessage] = useState(null)
  const [inactiveDes, setInactiveDes] = useState("")
  const [iframeUrl, setIframeUrl] = useState("")

  const currentUser = getCurrentUser()
  const userRole = currentUser?.role

  const telegramLink = {
    text: "کانال اطلاع رسانی واحد گاز جهت مجریان",
    url: "https://t.me/kurdvahedgas",
  }

  // Initialize on mount
  useEffect(() => {
    const { onGetUSERs } = props
    onGetUSERs()
    onGetClientSetting()

    // Initialize SignalR connection
    const initSocket = async () => {
      try {
        const connection = await initializeSocket()
        console.log("Socket connection:", connection)
      } catch (err) {
        console.error("Failed to initialize socket:", err)
      }
    }

    initSocket()

    // Handle bank return
    if (returnFromBank) {
      if (returnFromBank.indexOf("ok") !== -1) {
        props.onGetUserBalance()
        setReturnMessage("کیف پول با موفقیت شارژ شد")
      }
      if (returnFromBank.indexOf("error") !== -1) {
        setReturnMessage("شارژ کیف پول انجام نشد")
      }
    }
  }, [])

  // Update inactive description when client settings change
  useEffect(() => {
    setInactiveDes(clientSetting?.inactiveDes || "")
  }, [clientSetting])

  // Clear return message after 5 seconds
  useEffect(() => {
    if (returnMessage) {
      const timer = setTimeout(() => {
        setReturnMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [returnMessage])

  // Load notification messages
  useEffect(() => {
    const message0 =
      "طبق مصوبه جلسه با شرکت گاز استان کردستان،اصلاح ویا جایگزینی نقشه بعد از تایید نهایی توسط بازرس امکان پذیر نیست و لازم است پرونده مجددا تشکیل گردد"
    const message1 =
      "طبق مصوبه جلسه با شرکت گاز استان کردستان،تست فشار لوله رابط مابین کنتور و رگولاتور الزامیست"
    const message3 =
      "ویرایش مشخصات و نام مالک پس از ارسال پرونده به شرکت گاز امکان پذیر نیست"
    const message6 =
      "بعد از قبول طراحی پرونده ، ظرف مدت 72 ساعت نسبت به  اعلام نظر مرحله طراحی اقدام نمایید"
    const message7 = "عدم پذیرش نقشه های توکار در شش ماهه دوم سال 1404"

    const message = [message7, message6, message0, message1, message3]
    setInfoMessage(message)
  }, [])

  // Load Metabase iframe URL for admin
  useEffect(() => {
    if (userRole === "Administrator" || userRole === "Accountant") {
      const fetchIframeUrl = async () => {
        try {
          const response = await getDashboardToken()
          setIframeUrl(response?.iframeUrl || "")
        } catch (error) {
          console.error("Failed to fetch dashboard token:", error)
        }
      }
      fetchIframeUrl()
    }
  }, [userRole])

  // Determine if user can see Metabase
  const canSeeMetabase = () => {
    if (userRole === "Accountant") return true
    if (userRole !== "Administrator") return false
    const allowedUsers = ["admin2", "admin3", "taji"]
    return allowedUsers.includes(currentUser?.nameid)
  }

  // Render role-specific dashboard
  const renderDashboard = () => {
    switch (userRole) {
      case "Administrator":
        return (
          <AdminDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            iframeUrl={iframeUrl}
            inactiveDes={inactiveDes}
            setInactiveDes={setInactiveDes}
            clientSetting={clientSetting}
            onInactiveClient={onInactiveClient}
            clientLoading={clientLoading}
            userInfo={userInfo}
            showMetabase={canSeeMetabase()}
          />
        )
      case "Engineer":
        return (
          <EngineerDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
          />
        )
      case "Executor":
        return (
          <ExecutorDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
          />
        )
      case "Installer":
        return (
          <InstallerDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
          />
        )
      case "Nezarat":
        return (
          <GasCoDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
          />
        )
      case "Accountant":
        return (
          <AccountantDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
            iframeUrl={iframeUrl}
            showMetabase={canSeeMetabase()}
          />
        )
      default:
        return (
          <ExecutorDashboard
            infoMessage={infoMessage}
            telegramLink={telegramLink}
            userInfo={userInfo}
          />
        )
    }
  }

  return (
    <>
      <MetaTags>
        <title>داشبورد | واحد گاز </title>
      </MetaTags>
      <div style={{ padding: "0 16px" }}>
        {returnMessage && (
          <Alert
            message={returnMessage}
            type="info"
            showIcon
            closable
            onClose={() => setReturnMessage(null)}
            style={{ marginBottom: 16, borderRadius: 8 }}
          />
        )}
        <div className="page-title-box">{renderDashboard()}</div>
      </div>
    </>
  )
}

const mapStateToProps = ({ USERs, Client }) => ({
  lstUSERs: USERs.lstUSERs,
  userBalance: USERs.userBalance,
  userInfo: USERs.userInfo,
  clientSetting: Client.clientSetting,
  clientLoading: Client.loading,
})

const mapDispatchToProps = dispath => ({
  onGetUSERs: () => dispath(getUSERs()),
  onGetUserBalance: () => dispath(getUserBalance()),
  onGetClientSetting: () => dispath(getClientSetting()),
  onInactiveClient: data => dispath(inactiveClient(data)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(Dashboard))
